import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { useAppActions } from "store/AppSlice";
import { useAuthActions, getAuthFromLocalStorage } from "store/AuthSlice";

const getToken = () => {
  const auth = getAuthFromLocalStorage();
  return auth.accessToken;
};

const generateAnonUserAttributes = () => {
  return {
    data: {
      type: "users",
      attributes: {
        "first-name": "jibjab",
        password: "jibjab",
        role: "anonymous",
        "registration-source": "web",
        email: `${uuidv4()}@jibjab.com`,
      },
    },
  };
};

const useUsers = () => {
  const {
    loadCurrentUser,
    setCurrentUserLoadingStatus,
    createUser,
    updateUser,
    clearErrors,
    reactivateSubscription,
    deactivateSubscription,
  } = useAppActions();

  const authActions = useAuthActions();

  const { currentUser, currentUserLoadingStatus, errors } = useSelector((state) => state.AppSlice);

  const createAnonUser = () => {
    const attributes = generateAnonUserAttributes();
    return createUser(attributes);
  };

  const signIn = ({ password = "jibjab", email, username = email, relogin = false }) => {
    let attributes = {};
    if (currentUser && !relogin) {
      const anonymousToken = getToken();
      attributes = { grantType: "password", username, password, anonymousToken };
    } else {
      attributes = { grantType: "password", username, password };
    }

    return authActions.signIn(attributes);
  };

  const ensureCurrentUser = () => {
    if (currentUser) return Promise.resolve(currentUser);

    return createAnonUser()
      .then(({ attributes: { email: username } }) => signIn({ username }))
      .then(() => loadCurrentUser());
  };

  const fbSignIn = ({ accessToken }) => {
    const anonymousToken = getToken();
    const attributes = {
      grantType: "assertion",
      provider: "facebook",
      token: accessToken,
      ...(currentUser && anonymousToken),
    };
    return authActions.signIn(attributes);
  };

  const regularSignUp = ({ username, password }) => {
    if (currentUser) {
      const attributes = {
        data: {
          type: "users",
          attributes: {
            email: username,
            password,
            registrationSource: "web",
            role: "regular",
            firstName: "",
          },
        },
      };
      return updateUser(attributes);
    }
    const attributes = {
      data: {
        type: "users",
        attributes: {
          email: username,
          password,
          registrationSource: "web",
        },
      },
    };
    return createUser(attributes);
  };

  const fbSignUp = ({ name, email, password, id, accessToken }) => {
    const attributes = {
      data: {
        type: "users",
        attributes: {
          email,
          firstName: name,
          password,
          registrationSource: "web",
        },
        relationships: {
          identities: {
            data: [
              {
                type: "identities",
                attributes: {
                  provider: "facebook",
                  uid: id,
                  token: accessToken,
                },
              },
            ],
          },
        },
      },
    };
    return createUser(attributes);
  };

  return {
    loadCurrentUser,
    currentUserLoadingStatus,
    setCurrentUserLoadingStatus,
    currentUser,
    createAnonUser,
    updateUser,
    reactivateSubscription,
    deactivateSubscription,
    signIn,
    signOut: authActions.signOut,
    ensureCurrentUser,
    fbSignIn,
    regularSignUp,
    fbSignUp,
    errors,
    clearErrors,
  };
};

export default useUsers;
